import type { TransactionType, FilterableFeature, ProjectType, PropertyType } from '@@/shared/property/types';
import type { SearchPropertyType } from '@@/shared/property/search-criteria';
import type { Language, Dictionary } from '@@/shared/application';

export const TRANSACTION_TYPE_TRANSLATIONS: Dictionary<TransactionType> = {
  sale: {
    en: 'sale',
    fr: 'vente',
  },
  rental: {
    en: 'rental',
    fr: 'location',
  },
} as const;

// This is a reverse dictionary of all translations terms to their respective transaction type
export const TRANSLATED_TRANSACTION_TYPES = Object.fromEntries(
  Object
    .entries(TRANSACTION_TYPE_TRANSLATIONS)
    .map(([propertyType, propertyTypeTranslations]) => {
      return Object
        .values(propertyTypeTranslations)
        .map(translation => [translation, propertyType]);
    })
    .flat(),
) as Record<string, TransactionType>;

export const TRANSLATED_SALE = Object.values(TRANSACTION_TYPE_TRANSLATIONS.sale);
export const TRANSLATED_RENTAL = Object.values(TRANSACTION_TYPE_TRANSLATIONS.rental);
export function translateTransactionType(lang: Language, transactionType: TransactionType) {
  return TRANSACTION_TYPE_TRANSLATIONS[transactionType][lang];
}

const business: Record<Language, string> = {
  en: 'commercial',
  fr: 'local-commercial',
};

export const PROPERTY_TYPE_TRANSLATIONS: Dictionary<PropertyType | SearchPropertyType> = {
  'house': {
    en: 'housevilla',
    fr: 'maison',
  },
  'apartment': {
    en: 'apartment',
    fr: 'appartement',
  },
  'building': {
    en: 'building',
    fr: 'immeuble',
  },
  'land': {
    en: 'land',
    fr: 'terrain',
  },
  'parking': {
    en: 'parking',
    fr: 'parking',
  },
  'business-asset': business,
  'commercial-walls': business,
  'lease-hold': business,
  'business': business,
} as const;

export const PROPERTY_SEARCH_TYPE_TRANSLATIONS: Dictionary<SearchPropertyType> = {
  house: PROPERTY_TYPE_TRANSLATIONS.house,
  apartment: PROPERTY_TYPE_TRANSLATIONS.apartment,
  building: PROPERTY_TYPE_TRANSLATIONS.building,
  land: PROPERTY_TYPE_TRANSLATIONS.land,
  parking: PROPERTY_TYPE_TRANSLATIONS.parking,
  business: business,
} as const;

// This is a reverse dictionary of all translations terms to their respective property type
export const TRANSLATED_PROPERTY_SEARCH_TYPES = Object.fromEntries(
  Object
    .entries(PROPERTY_TYPE_TRANSLATIONS)
    .map(([propertyType, propertyTypeTranslations]) => {
      return Object
        .values(propertyTypeTranslations)
        .map(translation => [translation, propertyType]);
    })
    .flat(),
) as Record<string, SearchPropertyType>;

export function translatePropertyType(lang: Language, propertyType: PropertyType) {
  return PROPERTY_TYPE_TRANSLATIONS[propertyType][lang];
}

export const FEATURE_TRANSLATIONS: Dictionary<FilterableFeature> = {
  balcony: {
    en: 'balcony',
    fr: 'balcon',
  },
  cellar: {
    en: 'cellar',
    fr: 'cave',
  },
  elevator: {
    en: 'elevator',
    fr: 'ascenseur',
  },

  pool: {
    en: 'pool',
    fr: 'piscine',
  },
  garage: {
    en: 'garage',
    fr: 'place-parking',
  },
  terrace: {
    en: 'terrace',
    fr: 'terrasse',
  },
  // NOTE: garden/furnished deactivated until data is available
  // furnished: {
  //   en: 'furnished',
  //   fr: 'meuble',
  // },
  // garden: {
  //   en: 'garden',
  //   fr: 'jardin',
  // },
} as const;

// This is a reverse dictionary of all translations terms to their respective property type
export const TRANSLATED_FEATURES = Object.fromEntries(
  Object
    .entries(FEATURE_TRANSLATIONS)
    .map(([feature, featureTranslations]) => {
      return Object
        .values(featureTranslations)
        .map(translation => [translation, feature]);
    })
    .flat(),
) as Record<string, FilterableFeature>;

export const PROJECT_TYPE_TRANSLATIONS: Dictionary<ProjectType> = {
  new: {
    en: 'new',
    fr: 'neuf',
  },
  used: {
    en: 'used',
    fr: 'ancien',
  },
} as const;
export const TRANSLATED_PROJECT_TYPES = Object.fromEntries(
  Object
    .entries(PROJECT_TYPE_TRANSLATIONS)
    .map(([projectType, projectTypeTranslations]) => {
      return Object
        .values(projectTypeTranslations)
        .map(translation => [translation, projectType]);
    })
    .flat(),
) as Record<string, ProjectType>;
